import React, { useEffect, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getSelection, $isRangeSelection, createCommand, COMMAND_PRIORITY_EDITOR, $createTextNode } from 'lexical';
import { $createLinkNode, $isLinkNode, LinkNode } from '@lexical/link';
import { createPortal } from 'react-dom';

export const INSERT_LINK_COMMAND = createCommand('insertLink');
export const TOGGLE_LINK_EDITOR = createCommand('toggleLinkEditor');

interface LinkEditorProps {
  url: string;
  onSave: (url: string) => void;
  onCancel: () => void;
}

function LinkEditor({ url, onSave, onCancel }: LinkEditorProps) {
  const [linkUrl, setLinkUrl] = useState(url);
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      onSave(linkUrl);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };
  
  const handleSave = (e: React.MouseEvent) => {
    e.preventDefault();
    onSave(linkUrl);
  };
  
  const handleCancel = (e: React.MouseEvent) => {
    e.preventDefault();
    onCancel();
  };
  
  return (
    <div className="link-editor">
      <div className="link-input">
        <input 
          type="text" 
          value={linkUrl} 
          onChange={(e) => setLinkUrl(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="https://" 
          autoFocus
        />
        <div className="link-editor-buttons">
          <button type="button" onClick={handleSave}>Save</button>
          <button type="button" onClick={handleCancel}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

export default function LinkPlugin() {
  const [editor] = useLexicalComposerContext();
  const [isEditingLink, setIsEditingLink] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkPosition, setLinkPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  const handleLinkEdit = (url: string) => {
    setIsEditingLink(true);
    setLinkUrl(url);
    
    // Position editor near the selection
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      setLinkPosition({
        x: rect.left,
        y: rect.bottom + window.scrollY + 10,
      });
    }
  };

  const saveLinkEdit = (url: string) => {
    editor.update(() => {
      const selection = $getSelection();
      
      if ($isRangeSelection(selection)) {
        // If selection is collapsed or empty, don't create a link
        if (selection.isCollapsed()) {
          return;
        }
        
        // Check if we're editing an existing link node
        const nodes = selection.getNodes();
        const linkNode = nodes.find(node => $isLinkNode(node));
        
        if (linkNode && $isLinkNode(linkNode)) {
          // If this is an existing link node, just update its URL
          linkNode.setURL(url);
        } else {
          // Get the selected text before we modify the selection
          const selectedText = selection.getTextContent();
          
          // Create a new link node with the URL
          const linkNode = $createLinkNode(url);
          
          // If there's selected text, create a text node with that content
          if (selectedText) {
            const textNode = $createTextNode(selectedText);
            linkNode.append(textNode);
          }
          
          // Replace the selection with the link node
          selection.insertNodes([linkNode]);
        }
      }
    });
    setIsEditingLink(false);
  };

  // Register command listener for inserting links
  useEffect(() => {
    return editor.registerCommand(
      INSERT_LINK_COMMAND,
      (payload: string) => {
        if (typeof payload === 'string') {
          handleLinkEdit(payload);
        } else {
          handleLinkEdit('');
        }
        return true;
      },
      COMMAND_PRIORITY_EDITOR
    );
  }, [editor]);

  // Register command listener for toggling link editor
  useEffect(() => {
    return editor.registerCommand(
      TOGGLE_LINK_EDITOR,
      () => {
        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            // Find the link node by checking all nodes in the selection
            const nodes = selection.getNodes();
            let linkNode = null;
            
            // First, check if any of the selected nodes is a link node
            for (const node of nodes) {
              if ($isLinkNode(node)) {
                linkNode = node;
                break;
              }
            }
            
            // If no link node found in selection, check if we're inside a link
            if (!linkNode) {
              const anchorNode = selection.anchor.getNode();
              let parent = anchorNode.getParent();
              
              // Walk up the tree to find a link node
              while (parent) {
                if ($isLinkNode(parent)) {
                  linkNode = parent;
                  break;
                }
                parent = parent.getParent();
              }
            }
            
            if (linkNode) {
              handleLinkEdit(linkNode.getURL());
            } else {
              handleLinkEdit('');
            }
          }
        });
        return true;
      },
      COMMAND_PRIORITY_EDITOR
    );
  }, [editor]);

  return (
    <>
      {isEditingLink && createPortal(
        <div 
          style={{ 
            position: 'absolute', 
            left: `${linkPosition.x}px`, 
            top: `${linkPosition.y}px`,
            zIndex: 100,
          }}
        >
          <LinkEditor 
            url={linkUrl} 
            onSave={saveLinkEdit} 
            onCancel={() => setIsEditingLink(false)} 
          />
        </div>,
        document.body
      )}
    </>
  );
}